// adminportal/src/contexts/SettingsContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PortalSettings, DEFAULT_SETTINGS, AVAILABLE_THEMES, ThemeName } from '../types/settings';

interface SettingsContextType {
  settings: PortalSettings;
  updateSettings: (newSettings: Partial<PortalSettings>) => void;
  saveSettingsToApi: () => Promise<void>;
  resetToDefaults: () => void;
  clearLogoUrl: () => void;
  clearCacheAndReload: () => void;
  applyTheme: (themeName: ThemeName) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<PortalSettings>(DEFAULT_SETTINGS);

  // Apply theme to CSS variables
  const applyTheme = (themeName: ThemeName) => {
    const theme = AVAILABLE_THEMES.find(t => t.name === themeName) || AVAILABLE_THEMES[0];

    // Update CSS custom properties for comprehensive theming
    document.documentElement.style.setProperty('--color-primary', theme.primaryColor);
    document.documentElement.style.setProperty('--color-primary-hover', theme.primaryColor);
    document.documentElement.style.setProperty('--color-primary-focus', theme.primaryColor);

    // Apply secondary color as background
    document.documentElement.style.setProperty('--color-bg-primary', theme.secondaryColor);
    document.documentElement.style.setProperty('--color-bg-secondary', theme.secondaryColor);

    // Apply accent color for highlights
    document.documentElement.style.setProperty('--color-accent', theme.accentColor);
    document.documentElement.style.setProperty('--color-btn-primary', theme.accentColor);
    document.documentElement.style.setProperty('--color-btn-primary-hover', theme.accentColor);

    // For high contrast theme, ensure maximum visibility
    if (themeName === 'high-contrast') {
      document.documentElement.style.setProperty('--color-text-primary', '#FFFFFF');
      document.documentElement.style.setProperty('--color-text-secondary', '#FFFFFF');
      document.documentElement.style.setProperty('--color-bg-primary', '#000000');
      document.documentElement.style.setProperty('--color-bg-secondary', '#000000');
    }

    console.log(`🎨 Applied theme: ${theme.displayName}`, {
      primary: theme.primaryColor,
      secondary: theme.secondaryColor,
      accent: theme.accentColor,
    });
  };

  // Update favicon
  const updateFavicon = (faviconUrl?: string) => {
    const faviconLink = document.getElementById('favicon-link') as HTMLLinkElement;
    if (faviconLink && faviconUrl) {
      faviconLink.href = faviconUrl;
      console.log(`🎨 Updated favicon to: ${faviconUrl}`);
    }
  };

  // Load settings from localStorage or config.json
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // First, load config.json to get the correct company name
        let configCompanyName = DEFAULT_SETTINGS.company.name;
        try {
          const configResponse = await fetch('/config.json', { cache: 'no-store' });
          if (configResponse.ok) {
            const config = await configResponse.json();
            if (config.companyName) {
              configCompanyName = config.companyName;
            }
          }
        } catch (error) {
          console.log('No config.json found, using default company name');
        }

        // Load from localStorage for quick display
        let cachedSettings = null;
        const savedSettings = localStorage.getItem('portal-settings');
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          console.log('📦 localStorage settings found:', {
            logo: parsed.company?.logo,
            logoType: parsed.company?.logo
              ? parsed.company.logo.startsWith('data:')
                ? 'base64'
                : 'url'
              : 'none',
          });

          // Check if the cached title is outdated OR if the logo contains old API Gateway ID
          const hasOldApiGatewayLogo =
            parsed.company?.logo &&
            parsed.company.logo.includes('execute-api.us-east-1.amazonaws.com');
          if ((parsed.title && parsed.title !== DEFAULT_SETTINGS.title) || hasOldApiGatewayLogo) {
            console.log('🔄 Clearing outdated cached settings to prevent blinking');
            localStorage.removeItem('portal-settings');
          } else {
            cachedSettings = parsed;
          }
        } else {
          console.log('📦 No localStorage settings found');
        }

        // Set initial settings from cache or defaults for quick display
        // BUT don't set logo from cache - always get it from API
        const initialSettings = cachedSettings
          ? {
              ...DEFAULT_SETTINGS,
              ...cachedSettings,
              company: {
                ...DEFAULT_SETTINGS.company,
                ...cachedSettings.company,
                name: configCompanyName,
                logo: null, // Don't use cached logo - always get from API
              },
            }
          : {
              ...DEFAULT_SETTINGS,
              company: { ...DEFAULT_SETTINGS.company, name: configCompanyName },
            };

        setSettings(initialSettings);

        // Apply cached theme and favicon for quick display
        if (initialSettings.company.theme) {
          applyTheme(initialSettings.company.theme);
        }
        if (initialSettings.company.favicon) {
          updateFavicon(initialSettings.company.favicon);
        }

        // Always try to load fresh settings from API
        try {
          const configResponse = await fetch('/config.json', { cache: 'no-store' });
          if (configResponse.ok) {
            const config = await configResponse.json();

            // Try to load settings from API
            if (config.apiUrl && !config.apiUrl.includes('placeholder.invalid')) {
              try {
                const settingsResponse = await fetch(`${config.apiUrl}/settings`, {
                  cache: 'no-store',
                });
                if (settingsResponse.ok) {
                  const apiSettings = await settingsResponse.json();

                  // Map API response to our settings format
                  const newSettings = {
                    ...DEFAULT_SETTINGS,
                    company: {
                      ...DEFAULT_SETTINGS.company,
                      name: configCompanyName,
                      logo: apiSettings.logo || DEFAULT_SETTINGS.company.logo, // Use API logo or default
                      favicon: apiSettings.favicon || DEFAULT_SETTINGS.company.favicon,
                      theme: apiSettings.theme || DEFAULT_SETTINGS.company.theme,
                      primaryColor:
                        apiSettings.primaryColor || DEFAULT_SETTINGS.company.primaryColor,
                      secondaryColor:
                        apiSettings.secondaryColor || DEFAULT_SETTINGS.company.secondaryColor,
                      accentColor: apiSettings.accentColor || DEFAULT_SETTINGS.company.accentColor,
                    },
                    system: {
                      email: apiSettings.system?.email || DEFAULT_SETTINGS.system.email,
                    },
                  };

                  console.log('🔄 API settings loaded:', {
                    apiLogo: apiSettings.logo,
                    finalLogo: newSettings.company.logo,
                    logoType: apiSettings.logo
                      ? apiSettings.logo.startsWith('data:')
                        ? 'base64'
                        : 'url'
                      : 'none',
                  });

                  setSettings(newSettings);

                  // Apply the loaded theme
                  if (newSettings.company.theme) {
                    applyTheme(newSettings.company.theme);
                  }

                  // Apply the loaded favicon
                  if (newSettings.company.favicon) {
                    updateFavicon(newSettings.company.favicon);
                  }
                } else {
                  throw new Error('API settings not available');
                }
              } catch (apiError) {
                console.log('API settings not available, trying config.json fallback');

                // Fallback to config.json
                if (config.companySettings) {
                  const newSettings = { ...DEFAULT_SETTINGS, ...config.companySettings };
                  newSettings.company.name = configCompanyName;
                  newSettings.company.logo = '/logos/default/logo.svg';
                  setSettings(newSettings);

                  // Apply the loaded theme
                  if (newSettings.company.theme) {
                    applyTheme(newSettings.company.theme);
                  }

                  // Apply the loaded favicon
                  if (newSettings.company.favicon) {
                    updateFavicon(newSettings.company.favicon);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.log('No config.json found, using defaults');
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        // Error occurred, using default settings
        setSettings({
          ...DEFAULT_SETTINGS,
          company: { ...DEFAULT_SETTINGS.company, name: DEFAULT_SETTINGS.company.name },
        });
        applyTheme(DEFAULT_SETTINGS.company.theme);
      } finally {
        // Settings loading complete
      }
    };

    loadSettings();
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('portal-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (newSettings: Partial<PortalSettings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };

      // If theme changed, apply it immediately
      if (newSettings.company?.theme && newSettings.company.theme !== prev.company.theme) {
        applyTheme(newSettings.company.theme);
      }

      // If favicon changed, update it immediately
      if (newSettings.company?.favicon && newSettings.company.favicon !== prev.company.favicon) {
        updateFavicon(newSettings.company.favicon);
      }

      return updated;
    });
  };

  const resetToDefaults = () => {
    setSettings(DEFAULT_SETTINGS);
    applyTheme(DEFAULT_SETTINGS.company.theme);
  };

  const clearLogoUrl = () => {
    setSettings(prev => ({
      ...prev,
      company: {
        ...prev.company,
        logo: null,
      },
    }));
  };

  const clearCacheAndReload = () => {
    localStorage.removeItem('portal-settings');
    window.location.reload();
  };

  const saveSettingsToApi = async () => {
    try {
      console.log('🔄 saveSettingsToApi called with settings:', {
        logo: settings.company.logo ? 'present' : 'missing',
        logoType: settings.company.logo
          ? settings.company.logo.startsWith('data:')
            ? 'base64'
            : 'url'
          : 'none',
        logoLength: settings.company.logo ? settings.company.logo.length : 0,
      });

      const configResponse = await fetch('/config.json', { cache: 'no-store' });
      if (configResponse.ok) {
        const config = await configResponse.json();
        console.log('📡 Config loaded, API URL:', config.apiUrl);

        if (config.apiUrl) {
          // Map our settings to API format
          const apiSettings = {
            theme: settings.company.theme,
            primaryColor: settings.company.primaryColor,
            secondaryColor: settings.company.secondaryColor,
            accentColor: settings.company.accentColor,
            companyName: settings.company.name,
            logo: settings.company.logo,
            favicon: settings.company.favicon,
            enableInboundDownloads: settings.company.enableInboundDownloads,
            enableInboundNotifications: settings.company.enableInboundNotifications,
            system: {
              email: settings.system.email,
            },
          };

          console.log('📤 Sending to API:', {
            ...apiSettings,
            logo: apiSettings.logo ? `${apiSettings.logo.substring(0, 50)}...` : 'null',
          });

          const response = await fetch(`${config.apiUrl}/settings`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(apiSettings),
          });

          console.log('📡 API response status:', response.status);

          if (response.ok) {
            const responseData = await response.json();
            console.log('✅ Settings saved to API successfully:', responseData);
          } else {
            const errorText = await response.text();
            console.error('❌ Failed to save settings to API:', response.status, errorText);
          }
        } else {
          console.error('❌ No API URL found in config');
        }
      } else {
        console.error('❌ Failed to load config.json:', configResponse.status);
      }
    } catch (error) {
      console.error('❌ Error saving settings to API:', error);
    }
  };

  const value: SettingsContextType = {
    settings,
    updateSettings,
    saveSettingsToApi,
    resetToDefaults,
    clearLogoUrl,
    clearCacheAndReload,
    applyTheme,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
